import { Injectable, signal } from '@angular/core';

/**
 * The colour themes the app ships, in the order the picker offers them. Each
 * name is a `[data-theme='…']` block in `src/theme.css`, and the same list
 * lives in `backend/app/core/constants.py` so the account can store it.
 * The first is the default.
 */
export const THEMES = ['indigo', 'blue', 'teal', 'violet', 'slate'] as const;

export type ThemeName = (typeof THEMES)[number];

/** Read by the inline script in index.html too, so the page paints in the right colours before Angular starts. */
const STORAGE_KEY = 'schoolos.theme';

/**
 * Which colour theme the app is drawn in.
 *
 * A theme is nothing but a `data-theme` attribute on <html>: `theme.css`
 * swaps its variables for it, Tailwind's classes read those variables, and
 * every screen follows without re-rendering. So switching is instant, and
 * this service only has to set the attribute, remember the choice, and keep
 * the browser's own chrome (`<meta name="theme-color">`) in step.
 *
 * Kept per device in localStorage and per person on the account, the same
 * arrangement as the language: the device applies it at once, the account
 * carries it to the next device. Like the language it survives sign-out —
 * it is how somebody likes the app to look, not part of their session.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _current = signal<ThemeName>(this.stored() ?? THEMES[0]);

  /** The theme in force. */
  readonly current = this._current.asReadonly();

  constructor() {
    this.apply(this._current());
  }

  /** Switch theme. Immediate; nothing reloads. */
  use(name: ThemeName): void {
    if (name === this._current()) {
      return;
    }
    this._current.set(name);
    this.apply(name);
    try {
      localStorage.setItem(STORAGE_KEY, name);
    } catch {
      // Non-fatal: the choice simply will not survive a reload on this device.
    }
  }

  /**
   * Bring this device and the account into line at sign-in.
   *
   * * Nothing chosen on this device yet: take the account's theme, so a new
   *   machine looks the way the person left the last one.
   * * Chosen here and different: that is the newer choice — return it so the
   *   caller saves it to the account.
   *
   * Returns the theme to save, or null when there is nothing to do.
   */
  reconcile(accountTheme: string | null | undefined): ThemeName | null {
    const chosenHere = this.stored();
    if (chosenHere === null) {
      if (accountTheme && this.isTheme(accountTheme)) {
        this.use(accountTheme);
      }
      return null;
    }
    return chosenHere === accountTheme ? null : chosenHere;
  }

  isTheme(name: string): name is ThemeName {
    return (THEMES as readonly string[]).includes(name);
  }

  private apply(name: ThemeName): void {
    const root = document.documentElement;
    root.dataset['theme'] = name;
    // The address bar and task switcher on phones take their colour from this.
    // Read back from the variables rather than repeated here, so theme.css
    // stays the only place a colour is written down.
    const channels = getComputedStyle(root).getPropertyValue('--color-primary').trim();
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta && channels) {
      meta.content = `rgb(${channels.split(/\s+/).join(', ')})`;
    }
  }

  private stored(): ThemeName | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value && this.isTheme(value) ? value : null;
    } catch {
      return null;
    }
  }
}
